/**
 * @fileoverview Component that renders the show diff modal.
 *
 * @author Benjamin Leitner (bleitner@yext.com)
 */
goog.module('yext.m4.confcode.adminconsole.ShowDiffModal');

const {Checkbox} = goog.require('yext.ui.components.Checkbox');
const {ContentSkeleton} = goog.require('yext.ui.components.contentskeleton');
const DiagnosticData = goog.require('yext.m4.errors.DiagnosticData');

const {FullWidthDialog} = goog.require('yext.ui.components.modal.Dialog');

const DiffViewer = /** @type {ReactDiffViewerJS} */ (require('@mancusi/react-diff-viewer').default);

class ShowDiffModal extends React.Component {
  constructor(props) {
    super(props);
    this._renderModalBody = this._renderModalBody.bind(this);
    this._renderContentSkeleton = this._renderContentSkeleton.bind(this);
    this._renderErrorContainer = this._renderErrorContainer.bind(this);
    this._renderDiffContainer = this._renderDiffContainer.bind(this);
    this._showDialog = this._showDialog.bind(this);
    this._hideDialog = this._hideDialog.bind(this);

    /** @type {!State} */
    this.state = {
      showDialog: true,
      visibleResourceActions: new Set(['added', 'edited', 'removed']),
    };
  }

  /**
   * @private
   * Checks whether the resource diffs objects is empty.
   */
  _isResourceDiffsEmpty() {
    return !Object.keys(this.props.resourceDiffs).length;
  }

  /**
   * @private
   * Checks whether any resource in the diff is visible.
   *
   * @param {!Object} resourceActionsCount
   */
  _isAnyResourceVisible(resourceActionsCount) {
    const visibleResourceActions = this.state.visibleResourceActions;

    for (const action of Object.keys(resourceActionsCount)) {
      if (visibleResourceActions.has(action)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Gets the diagnostic represented as a string.
   *
   * @param {!DiagnosticData.Diagnostic} diagnostic
   * @param {!string} diagnosticType
   * @private
   */
  _getDiagnosticAsString(diagnostic, diagnosticType) {
    let text = `${diagnosticType.toUpperCase()}: `;
    if (diagnostic.fileLocation) {
      text += `${diagnostic.fileLocation.filePath}:`;
      if (diagnostic.fileLocation.coordinate.row !== 0
          && diagnostic.fileLocation.coordinate.column !== 0) {
        text += `${diagnostic.fileLocation.coordinate.row}:`;
        text += `${diagnostic.fileLocation.coordinate.column}:`;
      }
    }
    text += ` ${diagnostic.message}`;
    return text;
  }

  /**
   * @private
   * Renders the content skeleton when the resource diff is loading.
   */
  _renderContentSkeleton() {
    return (
      <div className="modal-body d-flex flex-column justify-content-center">
        <ContentSkeleton />
      </div>
    );
  }

  /**
   * @private
   * Renders the error container.
   */
  _renderErrorContainer() {
    const errors = this.props.errors.map(error =>
      <p key={error} className="r-error">
        {this._getDiagnosticAsString(error, 'error')}
      </p>,
    );

    return (
      <div className="modal-body">
        <div className="show-diff-error-container r-show-diff-error-container">
          {errors}
        </div>
      </div>
    );
  }

  /**
   * @private
   * Renders the diff container.
   */
  _renderDiffContainer() {
    // Render a no diffs message if no resource diffs are present
    if (this._isResourceDiffsEmpty()) {
      return (
        <div className="modal-body d-flex flex-row justify-content-start">
          <div className="no-diffs-message-container r-no-diffs-message-container d-flex flex-column justify-content-center">
            <p>There are no diffs to display</p>
          </div>
        </div>
      );
    }

    const resourceDiffs = this.props.resourceDiffs;
    const visibleResourceActions = this.state.visibleResourceActions;
    let formattedRelativePaths = [];

    let resourceActionsCount = {};
    for (const key of Object.keys(resourceDiffs)) {
      const action = resourceDiffs[key]['action'];
      if (resourceActionsCount[action]) {
        resourceActionsCount[action] = resourceActionsCount[action] + 1;
      } else {
        resourceActionsCount[action] = 1;
      }
    }

    return (
      <div className="modal-body d-flex flex-row justify-content-start">
        <div className="diff-filter">
          <div>
            <span className="diff-filter-icon"></span>
            <span>Resource Actions Filter</span>
          </div>

          <div className="resource-actions">
            {Object.keys(resourceActionsCount).map(action => {
              return (
                // eslint-disable-next-line
                <Checkbox
                  id={action + 'Resources'}
                  label={
                    action.charAt(0).toUpperCase() + action.slice(1) + // Capitalize first letter
                    ' (' + resourceActionsCount[action] + ')'}
                  checked={visibleResourceActions.has(action)}
                  value={action + 'Resources'}
                  onChange={() => {
                    if (!visibleResourceActions.delete(action)) {
                      visibleResourceActions.add(action);
                    }

                    this.setState({visibleResourceActions: visibleResourceActions});
                  }}
                ></Checkbox>
              );
            })}
          </div>
        </div>

        <div className="accordion r-accordion" id="resource-file-accordion">

          {!this._isAnyResourceVisible(resourceActionsCount)
            && <div className="no-diffs-message-container r-no-diffs-message-container d-flex flex-column justify-content-center">
              <p>There are no diffs to display</p>
            </div>
          }
          {Object.keys(resourceDiffs).map((relativePath, index) => {
            const diffObject = resourceDiffs[relativePath];
            const action = diffObject['action'];
            if (!visibleResourceActions.has(action)) {
              return;
            }

            // Replace all occurrences of '/', '.', '%', and ' ' with '_-_' for rendering purposes.
            let elementId = relativePath.replace(/(\/|\.|\%|\s)/g, '-') + '-collapse';

            // Continue to append dashes until the element ID is unique if necessary
            while (formattedRelativePaths.includes(elementId)) {
              elementId += '-';
            }
            formattedRelativePaths.push(elementId);

            const currentState = diffObject['currentState'];
            const newState = diffObject['newState'];

            return (
              // eslint-disable-next-line
              <div className='card r-card'>
                <div
                  className="card-header"
                  id={relativePath + '-heading'}
                  data-toggle="collapse"
                  data-target={'#' + elementId}
                  aria-expanded="false"
                  aria-controls={elementId}
                >
                  {relativePath}
                </div>

                <div
                  className="collapse"
                  id={elementId}
                  aria-labelledby={relativePath + '-heading'}
                >
                  <div className="card-body">
                    <DiffViewer
                      oldValue={currentState}
                      newValue={newState}
                      splitView={action === 'edited'}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  _showDialog() {
    this.setState({showDialog: true});
  }

  _hideDialog() {
    this.setState({showDialog: false});
  }

  componentDidUpdate(prevProps) {
    if (prevProps != this.props) {
      this._showDialog();
    }
  }

  /**
   * @private
   * Renders the body of the show diff modal.
   */
  _renderModalBody() {
    // If the diff is loading, render the content skeleton
    if (this.props.isLoading) {
      return this._renderContentSkeleton();
    } else if (this.props.errors.length !== 0) { // If the diff has errors, render the errors
      return this._renderErrorContainer();
    } else { // Otherwise, render the resource diffs
      return this._renderDiffContainer();
    }
  }

  render() {
    let callToAction;

    // Don't render the continue / cancel buttons, if there's no callback specified in props.
    if (!!this.props.continueAction) {
      callToAction = {
        primary: {
          type: 'active',
          text: 'Continue',
          callback: () => {
            this.props.continueAction();
            this._hideDialog();
          },
          disabled: !this.props.enableContinue,
          tid: 'sl-show-diff-continue',
        },
        cancel: {
          type: 'cancel',
          text: 'Cancel',
          tid: 'sl-show-diff-cancel',
        },
      };
    }

    return (
      <FullWidthDialog
        title="View Diff"
        callToAction={callToAction}
        show={this.state.showDialog}
        onHideDialog={this._hideDialog}
        tid="sl-show-diff-dialog"
      >
        <div id="show-diff-modal">
          {this._renderModalBody()}
        </div>
      </FullWidthDialog>
    );
  }
}

ShowDiffModal.propTypes = {
  resourceDiffs: PropTypes.object,
  isLoading: PropTypes.bool,
  errors: PropTypes.arrayOf(DiagnosticData.Diagnostic),
  continueAction: PropTypes.func,
  enableContinue: PropTypes.bool,
};

ShowDiffModal.defaultProps = {
  resourceDiffs: {},
  isLoading: false,
  errors: [],
  continueAction: null,
  enableContinue: true,
};

ShowDiffModal['defaultProps'] = ShowDiffModal.defaultProps;
ShowDiffModal['propTypes'] = ShowDiffModal.propTypes;

/** @record */
class State {
  constructor() {
    /**
     * @type {!boolean}
     */
    this.showDialog;

    /**
     * @type {!Set<string>}
     */
    this.visibleResourceActions;
  }
}

exports = ShowDiffModal;
